/**
 * AIDiscoveryBar — Unified search bar replacement for AI Discovery.
 * 
 * Replaces all existing search bars (NavBar SearchForm, FreshSearchBar,
 * and inline hero search forms) with a single AI-powered component.
 * 
 * Variants:
 *  - hero:   Large input with animated placeholder + suggestion pills (Home pages)
 *  - navbar: Compact input matching NavBar dimensions (desktop)
 *  - mobile: Full-width input in hamburger menu
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  Suspense,
  lazy,
  memo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import useAIDiscover from '../hooks/useAIDiscover';
import API from '../api';
import AIDiscoveryResults from './AIDiscoveryResults';
import { getMarketFromPath } from '../utils/aiDiscoverRouting';
import { trackEvent } from '../utils/analytics';
import './AIDiscoveryBar.css';

// Lazy-load Google sign-in for the auth gate
const GoogleOAuthProvider = lazy(() =>
  import('@react-oauth/google').then((m) => ({ default: m.GoogleOAuthProvider }))
);
const GoogleSignInButton = lazy(() => import('./GoogleSignInButton'));

const isReactSnap =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

// Animated placeholder examples (cycle every 3s)
const PLACEHOLDER_EXAMPLES_CANADA = [
  'Ask AI: Canadian-made skincare brands',
  'Ask AI: organic baby products from Canada',
  'Ask AI: sustainable home decor under $50',
  'Ask AI: Canadian gourmet food gifts',
  'Ask AI: women-owned beauty brands',
];

const PLACEHOLDER_EXAMPLES_GLOBAL = [
  'Ask AI: women-owned skincare under $40',
  'Ask AI: veteran-owned coffee brands',
  'Ask AI: sustainable home decor under $50',
  'Ask AI: vegan cruelty-free makeup',
  'Ask AI: organic baby products',
];

// Market-specific suggestion pills for hero (shown before first query)
const STATIC_SUGGESTIONS_CANADA = [
  { emoji: '🍁', label: 'Canadian-Made Skincare' },
  { emoji: '👩‍💼', label: 'Women-Owned Beauty' },
  { emoji: '🌿', label: 'Sustainable Home' },
];

const STATIC_SUGGESTIONS_GLOBAL = [
  { emoji: '👩‍💼', label: 'Women-Owned Beauty' },
  { emoji: '🌿', label: 'Sustainable Home' },
  { emoji: '🧴', label: 'Clean Skincare' },
];

const AIDiscoveryBar = memo(({
  variant = 'navbar',
  market: marketProp,
  placeholder,
  showSuggestions = true,
  autoFocus = false,
  className = '',
  onResultsNavigate,
  onCloseMenu,
  heroCtaSlot,
}) => {
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const hasFetchedSuggestions = useRef(false);
  const [inputValue, setInputValue] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [showSuggestionDropdown, setShowSuggestionDropdown] = useState(false);
  const [backendSuggestions, setBackendSuggestions] = useState(null);

  // Determine market from prop or path (must be before hooks that use it)
  const market = marketProp || getMarketFromPath(
    typeof window !== 'undefined' ? window.location.pathname : '/global/home'
  );

  // Fetch trending suggestions from backend (hero variant only, on first focus)
  const fetchSuggestionsOnce = useCallback(() => {
    if (variant !== 'hero') return;
    if (hasFetchedSuggestions.current) return;
    hasFetchedSuggestions.current = true;

    // Check sessionStorage cache first
    const cacheKey = `ai_discover_suggestions_${market}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setBackendSuggestions(JSON.parse(cached));
        return;
      } catch (e) { /* ignore bad cache */ }
    }

    API.post('/api/ai/discover', { query: 'trending popular products', market })
      .then(res => {
        const suggestions = res.data?.suggestions;
        if (suggestions?.length > 0) {
          setBackendSuggestions(suggestions);
          sessionStorage.setItem(cacheKey, JSON.stringify(suggestions));
        }
      })
      .catch(() => {}); // Silently fall back to static
  }, [variant, market]);

  const {
    results,
    isLoading,
    error,
    isRateLimited,
    isFallback,
    requiresLogin,
    promptCount,
    discover,
    discoverDebounced,
    clearResults,
    FREE_PROMPT_LIMIT,
  } = useAIDiscover({ market });

  // Cycle animated placeholder (hero variant)
  useEffect(() => {
    if (variant !== 'hero') return;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % (market === 'canada' ? PLACEHOLDER_EXAMPLES_CANADA : PLACEHOLDER_EXAMPLES_GLOBAL).length);
    }, 3000);
    return () => clearInterval(interval);
  }, [variant]);

  // Auto-focus for hero variant
  useEffect(() => {
    if (autoFocus && inputRef.current && !isReactSnap) {
      // Delay focus to prevent layout shift during hydration
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  // Determine placeholder text
  const currentPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    if (variant === 'hero') return (market === 'canada' ? PLACEHOLDER_EXAMPLES_CANADA : PLACEHOLDER_EXAMPLES_GLOBAL)[placeholderIndex];
    return 'Search products with AI...';
  }, [placeholder, variant, placeholderIndex, market]);

  // Handle submit → navigate to /discover?q=
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const query = inputValue.trim();
      if (!query) {
        if (inputRef.current) inputRef.current.focus();
        return;
      }

      // Track
      trackEvent('ai_discover_query', {
        query,
        market,
        source: variant,
      });

      // Close mobile menu if applicable
      if (onCloseMenu) onCloseMenu();

      // If fallback (AI disabled), redirect to keyword search
      if (isFallback) {
        const searchUrl = market === 'global'
          ? `/global/search?q=${encodeURIComponent(query)}`
          : `/country/canada/search?q=${encodeURIComponent(query)}`;
        navigate(searchUrl);
        return;
      }

      // Navigate to AI discover page
      if (onResultsNavigate) {
        onResultsNavigate(query);
      } else {
        const discoverUrl = market === 'global'
          ? `/global/discover?q=${encodeURIComponent(query)}`
          : `/country/canada/discover?q=${encodeURIComponent(query)}`;
        navigate(discoverUrl);
      }

      setInputValue('');
      setShowResults(false);
      clearResults();
    },
    [inputValue, market, variant, isFallback, navigate, onResultsNavigate, onCloseMenu, clearResults]
  );

  // Handle suggestion pill click
  const handleSuggestionClick = useCallback(
    (suggestion) => {
      const query = typeof suggestion === 'string' ? suggestion : suggestion.label;
      setInputValue(query);

      trackEvent('ai_discover_suggestion', {
        suggestion: query,
        source: 'static',
      });

      // Navigate directly to discover page
      const discoverUrl = market === 'global'
        ? `/global/discover?q=${encodeURIComponent(query)}`
        : `/country/canada/discover?q=${encodeURIComponent(query)}`;
      
      if (onCloseMenu) onCloseMenu();
      navigate(discoverUrl);
      setInputValue('');
      setShowResults(false);
    },
    [market, navigate, onCloseMenu]
  );

  // Handle "View all results" from dropdown
  const handleViewAll = useCallback(
    (query) => {
      trackEvent('ai_discover_view_all', {
        query,
        resultCount: results?.products?.length || 0,
      });

      const discoverUrl = market === 'global'
        ? `/global/discover?q=${encodeURIComponent(query)}`
        : `/country/canada/discover?q=${encodeURIComponent(query)}`;

      if (onCloseMenu) onCloseMenu();
      navigate(discoverUrl);
      setShowResults(false);
      clearResults();
    },
    [market, results, navigate, onCloseMenu, clearResults]
  );

  // Handle input change — only do live typeahead for navbar variant (not hero/mobile)
  const handleInputChange = useCallback(
    (e) => {
      const val = e.target.value;
      setInputValue(val);

      // Hero variant: no live search — only search on Enter/submit to save prompts
      if (variant === 'hero' || variant === 'mobile') {
        if (!val.trim()) {
          setShowResults(false);
          clearResults();
        }
        return;
      }

      // Navbar variant: live typeahead preview
      if (val.trim().length >= 3) {
        setShowResults(true);
        discoverDebounced(val.trim());
      } else if (!val.trim()) {
        setShowResults(false);
        clearResults();
      }
    },
    [variant, discoverDebounced, clearResults]
  );

  // Handle dropdown suggestion click (from AI results)
  const handleResultSuggestionClick = useCallback(
    (suggestion) => {
      setInputValue(suggestion);
      trackEvent('ai_discover_suggestion', {
        suggestion,
        source: 'dynamic',
      });

      const discoverUrl = market === 'global'
        ? `/global/discover?q=${encodeURIComponent(suggestion)}`
        : `/country/canada/discover?q=${encodeURIComponent(suggestion)}`;
      
      navigate(discoverUrl);
      setShowResults(false);
      clearResults();
    },
    [market, navigate, clearResults]
  );

  // React-Snap: render a static placeholder
  if (isReactSnap) {
    return (
      <div className={`ai-discovery-bar ai-discovery-bar--${variant} ${className}`}>
        <form className="ai-discovery-form" role="search">
          <input
            type="text"
            placeholder={currentPlaceholder}
            className="ai-discovery-input"
            readOnly
            aria-label="Discover products, brands, and values"
          />
          <button type="submit" className="ai-discovery-submit" aria-label="Discover">
            <svg className="ai-discovery-search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
            </svg>
          </button>
        </form>
      </div>
    );
  }

  return (
    <div
      className={`ai-discovery-bar ai-discovery-bar--${variant} ${className}`}
      data-market={market}
      ref={wrapperRef}
    >
      <form className="ai-discovery-form" role="search" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={currentPlaceholder}
          className={`ai-discovery-input ${isLoading ? 'ai-discovery-input--loading' : ''}`}
          aria-label="Discover products, brands, and values"
          aria-expanded={showResults}
          aria-controls="ai-discovery-results"
          autoComplete="off"
          disabled={false}
          onFocus={() => { setShowSuggestionDropdown(true); fetchSuggestionsOnce(); }}
          onBlur={() => setTimeout(() => setShowSuggestionDropdown(false), 200)}
        />
        <button
          type="submit"
          className="ai-discovery-submit"
          aria-label="Discover"
        >
          <svg className="ai-discovery-search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
          </svg>
        </button>
      </form>

      {/* Remaining prompt indicator for guests */}
      {!user && promptCount >= 3 && promptCount < FREE_PROMPT_LIMIT && variant === 'hero' && (
        <p className="ai-discovery-remaining">
          {FREE_PROMPT_LIMIT - promptCount} AI search{FREE_PROMPT_LIMIT - promptCount !== 1 ? 'es' : ''} left · <span className="sign-in-hint">Sign in for unlimited</span>
        </p>
      )}

      {/* Prominent hero CTA slot (e.g. Build a Baby Registry) — above example chips */}
      {variant === 'hero' && heroCtaSlot && !inputValue.trim() && !results && !showResults && (
        <div className="ai-discovery-hero-cta-slot">
          {heroCtaSlot}
        </div>
      )}

      {/* Suggestion chips (hero variant, pre-query) */}
      {variant === 'hero' && showSuggestions && !inputValue.trim() && !results && !showResults && (
        <div className="ai-discovery-suggestion-chips">
          {backendSuggestions
            ? backendSuggestions.slice(0, 5).map((suggestion, i) => (
                <button
                  key={i}
                  className="ai-suggestion-chip"
                  onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(suggestion); }}
                  type="button"
                >
                  🔍 {suggestion}
                </button>
              ))
            : (market === 'canada' ? STATIC_SUGGESTIONS_CANADA : STATIC_SUGGESTIONS_GLOBAL).map((s, i) => (
                <button
                  key={i}
                  className="ai-suggestion-chip"
                  onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s); }}
                  type="button"
                >
                  {s.emoji} {s.label}
                </button>
              ))
          }
        </div>
      )}

      {/* Login gate */}
      {showResults && requiresLogin && (
        <div className="ai-results-dropdown" id="ai-discovery-results">
          <div className="ai-discovery-login-gate">
            <div className="login-gate-icon">✨</div>
            <h3 className="login-gate-title">You're loving AI Discovery!</h3>
            <p className="login-gate-message">
              Sign in with Google to unlock unlimited AI-powered product search — 
              plus save favorites, create collections, and get personalized recommendations.
            </p>
            <Suspense fallback={<div style={{ padding: '10px', textAlign: 'center' }}>Loading...</div>}>
              <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                <GoogleSignInButton
                  setUser={setUser}
                  onSignInSuccess={() => {
                    // promptCount resets automatically via useEffect in useAIDiscover
                    trackEvent('ai_discover_login_gate_signin', {
                      promptCount,
                      market,
                    });
                    if (inputValue.trim()) {
                      discover(inputValue.trim());
                    }
                  }}
                />
              </GoogleOAuthProvider>
            </Suspense>
            <p className="login-gate-counter">
              You've used all {FREE_PROMPT_LIMIT} guest searches.
            </p>
          </div>
        </div>
      )}

      {/* Inline results dropdown (not shown when login gate is active) */}
      {showResults && !requiresLogin && (results || isLoading || error || isRateLimited) && (
        <AIDiscoveryResults
          results={results}
          isLoading={isLoading}
          error={error}
          isRateLimited={isRateLimited}
          query={inputValue.trim()}
          market={market}
          onClose={() => {
            setShowResults(false);
            clearResults();
          }}
          onSuggestionClick={handleResultSuggestionClick}
          onViewAll={handleViewAll}
        />
      )}
    </div>
  );
});

AIDiscoveryBar.displayName = 'AIDiscoveryBar';

export default AIDiscoveryBar;
